import Split from "react-split"
import "../styles/Resizer.css"
import {Box, Dialog, useTheme} from "@mui/material"
import Manager from "../components/workspace/Manager"
import { useSelector} from "react-redux"
import {createContext, useContext, useEffect, useState} from "react"
import {
	selectPrimaryWorkspaceId,
	selectSecondaryWorkspace,
	selectSecondaryWorkspaceId,
	selectShowSecondaryWorkspace,
	selectWorkspace
} from "../reducers/workspaceSlice"
import DynamicPanel from "../components/dynamic/DynamicPanel"
import WorkspacePanel from "../components/workspace/WorkspacePanel"
import ContactSupport from "../components/dashboard/ContactSupport"
import { useSnackbar } from "notistack"
import UpgradeWithKeyModal from "../components/dashboard/UpgradeWithKeyModal"
import {useLocation, useSearchParams} from "react-router-dom"
import {selectLoadingUser} from "../reducers/userSlice"
import { InstructionsHelp } from "../components/dashboard/helpModal/InstructionsHelp"
import useDragContainer from "../hooks/useDragContainer"
import {DashboardMenuPortal} from "../components/DashboardMenu"
import {
	connectAllSocketServices,
	disconnectAllSocketServices,
} from "../context/socket.context"
import {useAuth0} from "@auth0/auth0-react"

const WorkspaceContext = createContext(null)

export const PanelProvider = WorkspaceContext.Provider

/**
 * Splits a composite workspace key into its panel slot and workspace ID.
 * Used in reducers, thunks, and middleware where the context hook isn't available.
 *
 * @param {string} compositeKey - The composite key (e.g., "primary-307")
 * @returns {{ panelSlot: string, workspaceId: number }}
 */
export function splitWorkspaceCompositeKey(compositeKey) {
	const [panelSlot, workspaceId] = compositeKey.split("-")
	return { panelSlot, workspaceId: Number(workspaceId) }
}

/**
 * Retrieves workspace identity from the nearest PanelProvider context.
 *
 * The context stores a composite key in the format "panelSlot-workspaceId"
 * (e.g., "primary-307"). This hook parses that key and returns all three
 * values so consumers can choose the appropriate identifier:
 *
 * - Use `workspaceId` for API calls, socket events, and any server-facing
 *   operation where the backend needs the actual workspace ID.
 * - Use `compositeKey` for Redux state lookups where each panel needs its
 *   own independent state bucket, even when both panels show the same
 *   workspace (e.g., selected article, scroll position, expanded nodes).
 * - Use `panelSlot` when component behavior or styling differs between
 *   primary and secondary panels.
 *
 * @returns {{ compositeKey: string, workspaceId: number, panelSlot: string }}
 * @property {string} compositeKey - The full context value (e.g., "primary-307").
 *   Used as the key for panel-scoped Redux state in byWorkspace maps.
 * @property {number} workspaceId - The numeric workspace ID extracted from the
 *   composite key. Used for API calls and shared workspace-level Redux state.
 * @property {string} panelSlot - Either "primary" or "secondary". Identifies
 *   which panel this component belongs to.
 */
export function useWorkspaceContext() {
	const compositeKey = useContext(WorkspaceContext)
	const { panelSlot, workspaceId } = splitWorkspaceCompositeKey(compositeKey)
	return {compositeKey, workspaceId, panelSlot}
}

export default function Dashboard() {
	useTheme()
	const primaryWorkspace = useSelector(selectWorkspace)
	const userIsLoading = useSelector(selectLoadingUser)
	const { search } = useLocation()
	const queryParams = new URLSearchParams(search)
	const keyId = queryParams.get("key")
	const pricing = queryParams.get("pricing")
	const { enqueueSnackbar } = useSnackbar()
	const [openKeyUpgradeModal, setOpenKeyUpgradeModal] = useState((!!pricing && pricing === "key-upgrade") && !!keyId)
	const changeUrlParams = useSearchParams()[1]
	const closeKeyUpgrade = () => {
		queryParams.delete("key")
		queryParams.delete("pricing")
		changeUrlParams(queryParams)
		setOpenKeyUpgradeModal(false)
	}
	const showSecondaryWorkspace = useSelector(selectShowSecondaryWorkspace)
	const primaryWorkspaceId = useSelector(selectPrimaryWorkspaceId)
	const secondaryWorkspaceId = useSelector(selectSecondaryWorkspaceId)
	const secondaryWorkspace = useSelector(selectSecondaryWorkspace)

	useEffect(() => {
		window.ondragover = function(e) {
			e.stopPropagation()
			e.preventDefault()
			return false 
		}
		window.ondrop = function(e) { 
			e.stopPropagation()
			e.preventDefault()
			return false 
		}
	}, [])

	// Call the useDragContainer hook
	useDragContainer()

	return primaryWorkspace === null ?
		(
			<Box sx={{ height: "100vh", backgroundColor: "primary.main" }}>
				<Dialog
					sx={{
						zIndex: 1302, //2 higher than the MUI default for "Dialog" which is used in Manager as well
					}}
					open={!userIsLoading && openKeyUpgradeModal}
					disableEscapeKeyDown
					fullWidth
				>
					<UpgradeWithKeyModal
						close={closeKeyUpgrade}
						keyId={keyId}
						enqueueSnackbar={enqueueSnackbar}
					/>
				</Dialog>
				<Manager noWorkspace={true}
				/>
			</Box>
		)
		:
		(
			//NOTE: cursor style can be set on this div when dragging note between panels
			<Box id={"dashboard-wrapper"} sx={{ backgroundColor: "panels.main" }} >
				{/*
				TODO: formalize this as a component. Eventually we want to hook it up to "superadmin" tools rerebder test?
				<Alert
					severity={"error"}
				>
					<AlertTitle>PubMed is currently experiencing issues.</AlertTitle>
					Searches, including Automated Searches, may experience issues until PubMed services are restored. Your data is safe, and you can continue use RefZoom to manage your imported citations.
				</Alert>
				*/}
				<SocketConnections />
				<DashboardMenuPortal onDashboard={true}/>
				<div id={"panel-container"}>
					<Split
						id={"panel-container"}
						key={"vertical-panel-container"}
						sizes={[50, 50]}
						minSize={showSecondaryWorkspace ? [80, 81] : 80}
						direction="vertical"
						gutterAlign={"center"}
						gutterSize={showSecondaryWorkspace ? 5 : 0}
					>

						<PanelProvider value={`primary-${primaryWorkspaceId}`}>
							<Split
								key={"horiz-panel-container-primary"}
								className="wrap main-workspace"
								sizes={[50, 50]}
								minSize={100}
								expandToMin={false}
								gutterSize={5}
								gutterAlign="center"
								snapOffset={30}
								dragInterval={1}
								direction="horizontal"
							>
								<WorkspacePanel workspace={primaryWorkspace} />
								<DynamicPanel workspace={primaryWorkspace} />
							</Split>
						</PanelProvider>
						<div
							id={"secondary-panel-container"}
							style={{
								display: showSecondaryWorkspace ? "block" : "none",
							}}
						>
							{showSecondaryWorkspace && (
								<PanelProvider value={`secondary-${secondaryWorkspaceId}`}>
									<Split
										key={"horiz-panel-container-secondary"}
										className="wrap secondary-workspace"
										sizes={[50, 50]}
										minSize={100}
										expandToMin={false}
										gutterSize={5}
										gutterAlign="center"
										snapOffset={30}
										dragInterval={1}
										direction="horizontal"
										cursor="col-resize"
									>
										<WorkspacePanel workspace={secondaryWorkspace} />
										<DynamicPanel workspace={secondaryWorkspace} />
									</Split>
								</PanelProvider>
							)}
						</div>
					</Split>
				</div>
				{/* WORKSPACE AGNOSTIC MODALS */}
				<Manager />
				<ContactSupport />
				<InstructionsHelp />
			</Box>
		)
}

/**
 * Owns connect/disconnect for all Socket.IO services. The services are
 * created at module load with `autoConnect: false`, so they stay inert until
 * this component mounts.
 *
 * Mount exactly one instance inside `<Dashboard />`. `<Dashboard />` is gated
 * by `<ProtectedRoute>`, so sockets only open after Auth0 has authenticated
 * the user. Unmount (navigation away, logout) disconnects all six.
 *
 * Renders `null`.
 *
 * Note: in dev StrictMode, the effect double-invokes (connect → disconnect →
 * connect). Socket.IO handles this cleanly: https://socket.io/how-to/use-with-react#remarks-about-the-useeffect-hook
 *
 * @component
 * @returns {null}
 */
function SocketConnections() {
	const { getAccessTokenSilently} = useAuth0()
	useEffect(() => {
		connectAllSocketServices(getAccessTokenSilently)
		return () => {
			disconnectAllSocketServices()
		}
	}, [getAccessTokenSilently])
	return null
}
